/**
 * Operating-system notifications (the ones that appear in macOS Notification Center / Windows
 * Action Center), via the browser's Notification API. They need one permission grant, asked
 * only when the person switches them on in Dynamic Island settings.
 */

export type OsPermission = 'granted' | 'denied' | 'default' | 'unsupported'

export function osPermission(): OsPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
  return Notification.permission
}

export async function requestOsPermission(): Promise<OsPermission> {
  if (osPermission() === 'unsupported') return 'unsupported'
  try {
    return await Notification.requestPermission()
  } catch {
    return osPermission()
  }
}

/**
 * Show one OS notification. Uses the service worker when there is one (works when the app is
 * installed and the tab is in the background), else the page-level API. `tag` collapses repeats.
 */
export async function showOsNotification(args: { title: string; body?: string | null; url?: string | null; tag: string }): Promise<void> {
  if (osPermission() !== 'granted') return
  const options: NotificationOptions = {
    body: args.body ?? undefined,
    tag: args.tag,
    icon: '/pwa/icon-192.png',
    badge: '/pwa/icon-maskable-192.png',
    data: { url: args.url ?? '/' },
  }
  try {
    const reg = await navigator.serviceWorker?.getRegistration()
    if (reg) {
      await reg.showNotification(args.title, options)
      return
    }
  } catch {
    // Fall through to the page-level API.
  }
  try {
    const n = new Notification(args.title, options)
    n.onclick = () => {
      window.focus()
      if (args.url) window.location.assign(args.url)
      n.close()
    }
  } catch {
    // Some browsers only allow notifications from a service worker; nothing else to do.
  }
}
